import type { OnLogEvent } from '../types/logger';

// ============================================================================
// Types
// ============================================================================

export type PopupWaitResult =
  | { type: 'message'; data: any }
  | { type: 'closed' };

export interface WaitForResultOptions {
  /** Called for every same-origin message; use for intermediate messages (e.g. conversionEventId). */
  onMessage?: (data: any) => void;
  /** Return true when the message is the final one (ok or error). */
  checkComplete: (response: any) => boolean;
}

export interface PaymentPopup {
  /** The underlying browser window (null if blocked). */
  readonly window: Window | null;
  /** Navigate the popup to a URL. */
  navigateTo: (url: string) => void;
  /** Wait for a postMessage result or the user closing the popup. */
  waitForResult: (options: WaitForResultOptions) => Promise<PopupWaitResult>;
  /** Remove message listeners and polling interval (keeps the window open). */
  cleanup: () => void;
  /** Close the popup window and clean up listeners. */
  close: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

const CLOSE_CHECK_INTERVAL_MS = 500;

export const getPopupWindowFeatures = (
  width = window.innerWidth,
  height = window.innerHeight
): string => {
  const {
    outerWidth,
    outerHeight,
    screenX,
    screenY
  } = window.top || window;

  const windowTop = outerHeight / 2 + screenY - height / 2;
  const windowLeft = outerWidth / 2 + screenX - width / 2;
  return `left=${ windowLeft },top=${ windowTop },width=${ width },height=${ height },menubar=no,location=no,toolbar=no,centerscreen=yes`;
};

// ============================================================================
// Factory
// ============================================================================

/**
 * Create a payment popup synchronously inside a user gesture handler.
 *
 * Safari blocks window.open() outside the synchronous click stack, so
 * popup-based gateways (PayPal) must call this during the click event.
 * The returned object exposes methods to navigate, wait for results,
 * and clean up.
 */
export const createPaymentPopup = (log?: OnLogEvent): PaymentPopup => {
  const popupWindow: Window | null = window.open('about:blank', 'payment_popup', getPopupWindowFeatures());

  let intervalId: NodeJS.Timeout | null = null;
  let removeMessageListener: (() => void) | null = null;

  const cleanup = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (removeMessageListener) {
      removeMessageListener();
      removeMessageListener = null;
    }
  };

  const popup: PaymentPopup = {
    // Stub — overridden below with a non-enumerable getter so React DevTools
    // never tries to traverse the cross-origin Window after PayPal navigates away.
    window: null,

    navigateTo(url: string) {
      log?.('info', 'Payment Processing', 'Navigating popup window', { url });
      if (popupWindow) {
        popupWindow.location.href = url;
      }
    },

    waitForResult(options: WaitForResultOptions): Promise<PopupWaitResult> {
      return new Promise((resolve) => {
        if (!popupWindow) {
          resolve({ type: 'closed' });
          return;
        }

        let resolved = false;

        const onMessage = (event: MessageEvent) => {
          if (resolved) return;
          if (event.origin !== window.location.origin) return;

          options.onMessage?.(event.data);

          if (options.checkComplete(event.data)) {
            resolved = true;
            cleanup();
            resolve({ type: 'message', data: event.data });
          }
        };

        window.addEventListener('message', onMessage);
        removeMessageListener = () => window.removeEventListener('message', onMessage);

        intervalId = setInterval(() => {
          if (resolved) return;
          if (popupWindow.closed) {
            resolved = true;
            cleanup();
            resolve({ type: 'closed' });
          }
        }, CLOSE_CHECK_INTERVAL_MS);
      });
    },

    cleanup,

    close() {
      cleanup();
      if (popupWindow && !popupWindow.closed) {
        popupWindow.close();
      }
    }
  };

  // Replace the stub with a non-enumerable getter. React DevTools traverses
  // enumerable props when a component mounts — once the popup has navigated
  // to PayPal's domain it becomes cross-origin, and any read from it throws a
  // SecurityError. Hiding 'window' from enumeration prevents DevTools from
  // ever touching it while keeping paymentPopup.window fully accessible.
  Object.defineProperty(popup, 'window', {
    get: () => popupWindow,
    enumerable: false,
    configurable: true
  });

  return popup;
};
